import { Temporal } from 'temporal-polyfill';
import { CoachSessionRemarkV2 } from './data/session-shared.js';
import { endOfDay, FrequentTimeZones, InstantCompare } from './temporal-utils/index.js';

export type TeachingHoursSessionInput = {
  start: Temporal.Instant;
  end: Temporal.Instant;
  coachSessionRemarksV2: CoachSessionRemarkV2[];
  sessionFeedbacks: Array<{ feedbackSubmitted: boolean }>;
};

export type WeeklyTeachingHoursWeek = {
  weekStart: Temporal.Instant;
  weekEnd: Temporal.Instant;
  minutes: number;
  sessions: number;
};

/**
 * Monday 00:00 in Pacific Time for the week that contains `instant`.
 */
export function startOfPacificWeek(instant: Temporal.Instant): Temporal.Instant {
  const zoned = instant.toZonedDateTimeISO(FrequentTimeZones.UsPacific);
  const daysFromMonday = zoned.dayOfWeek - 1; // ISO: Monday = 1 … Sunday = 7
  const monday = zoned.toPlainDate().subtract({ days: daysFromMonday });
  return monday
    .toZonedDateTime({ timeZone: FrequentTimeZones.UsPacific, plainTime: Temporal.PlainTime.from('00:00') })
    .toInstant();
}

/**
 * Sunday end-of-day (last ms) in Pacific Time for the week that contains `instant`.
 */
export function endOfPacificWeek(instant: Temporal.Instant): Temporal.Instant {
  const weekStart = startOfPacificWeek(instant);
  const sunday = weekStart.toZonedDateTimeISO(FrequentTimeZones.UsPacific).toPlainDate().add({ days: 6 });
  return endOfDay(
    sunday
      .toZonedDateTime({ timeZone: FrequentTimeZones.UsPacific, plainTime: Temporal.PlainTime.from('00:00') })
      .toInstant(),
    FrequentTimeZones.UsPacific,
  );
}

export function pacificWeekBounds(instant: Temporal.Instant): {
  weekStart: Temporal.Instant;
  weekEnd: Temporal.Instant;
} {
  return {
    weekStart: startOfPacificWeek(instant),
    weekEnd: endOfPacificWeek(instant),
  };
}

export function baseSessionMinutes(start: Temporal.Instant, end: Temporal.Instant): number {
  return start.until(end).round({ largestUnit: 'minute', smallestUnit: 'minute' }).minutes;
}

export function teachingBufferMinutes(baseMinutes: number): number {
  if (baseMinutes === 30) return 5;
  if (baseMinutes === 40) return 10;
  return 0;
}

function shouldKeepTeachingBuffer(session: TeachingHoursSessionInput, now: Temporal.Instant): boolean {
  // Future sessions always keep the buffer.
  if (InstantCompare(session.start).isAfter(now)) {
    return true;
  }

  // Past sessions are not recalculated until 24h after end — keep buffer until then.
  const recalculationAt = session.end.add({ hours: 24 });
  if (InstantCompare(now).isBefore(recalculationAt)) {
    return true;
  }

  const remarks = session.coachSessionRemarksV2;
  if (remarks.includes(CoachSessionRemarkV2.canceledWithin12Hours)) {
    return true;
  }

  const noLateOrEarly =
    !remarks.includes(CoachSessionRemarkV2.lateStart) && !remarks.includes(CoachSessionRemarkV2.endEarly);
  const allFeedbackSent = session.sessionFeedbacks.every((f) => f.feedbackSubmitted);
  return noLateOrEarly && allFeedbackSent;
}

/**
 * Teaching minutes for a single session, including the 5/10 minute buffer when earned.
 */
export function teachingMinutesForSession(
  session: TeachingHoursSessionInput,
  now: Temporal.Instant = Temporal.Now.instant(),
): number {
  const base = baseSessionMinutes(session.start, session.end);
  const buffer = teachingBufferMinutes(base);
  if (buffer === 0) {
    return base;
  }
  return shouldKeepTeachingBuffer(session, now) ? base + buffer : base;
}

export function teachingMinutesForSessions(
  sessions: TeachingHoursSessionInput[],
  now: Temporal.Instant = Temporal.Now.instant(),
): number {
  return sessions.reduce((sum, session) => sum + teachingMinutesForSession(session, now), 0);
}

/**
 * Sessions whose start falls within `[periodStart, periodEnd]` (inclusive).
 */
export function sessionsInPeriod(
  sessions: TeachingHoursSessionInput[],
  periodStart: Temporal.Instant,
  periodEnd: Temporal.Instant,
): TeachingHoursSessionInput[] {
  return sessions.filter(
    (session) =>
      InstantCompare(session.start).isAfterOrEqual(periodStart) &&
      InstantCompare(session.start).isBeforeOrEqual(periodEnd),
  );
}

/**
 * Sum teaching minutes for sessions whose start falls within `[periodStart, periodEnd]` (inclusive).
 */
export function countTeachingMinutesInPeriod(
  sessions: TeachingHoursSessionInput[],
  periodStart: Temporal.Instant,
  periodEnd: Temporal.Instant,
  now: Temporal.Instant = Temporal.Now.instant(),
): number {
  return teachingMinutesForSessions(sessionsInPeriod(sessions, periodStart, periodEnd), now);
}

/** A week at or above this many teaching minutes is flagged as an over-booked week. */
export const WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES = 27 * 60;

/**
 * Current and future Pacific weeks with at least `thresholdMinutes` of teaching, oldest first.
 * The current week counts its already-taught sessions, so it can qualify mid-week.
 */
export function weeksOverTeachingMinutes(
  sessions: TeachingHoursSessionInput[],
  thresholdMinutes: number = WEEKLY_TEACHING_HOURS_THRESHOLD_MINUTES,
  now: Temporal.Instant = Temporal.Now.instant(),
): WeeklyTeachingHoursWeek[] {
  const currentWeekStart = startOfPacificWeek(now);
  const sessionsByWeekStart = new Map<string, TeachingHoursSessionInput[]>();

  for (const session of sessions) {
    if (InstantCompare(session.start).isBefore(currentWeekStart)) {
      continue;
    }
    const weekStartIso = startOfPacificWeek(session.start).toString();
    const weekSessions = sessionsByWeekStart.get(weekStartIso);
    if (weekSessions) {
      weekSessions.push(session);
    } else {
      sessionsByWeekStart.set(weekStartIso, [session]);
    }
  }

  return [...sessionsByWeekStart.entries()]
    .map(([weekStartIso, weekSessions]) => {
      const weekStart = Temporal.Instant.from(weekStartIso);
      return {
        weekStart,
        weekEnd: endOfPacificWeek(weekStart),
        minutes: teachingMinutesForSessions(weekSessions, now),
        sessions: weekSessions.length,
      };
    })
    .filter((week) => week.minutes >= thresholdMinutes)
    .sort((a, b) => Temporal.Instant.compare(a.weekStart, b.weekStart));
}

/**
 * Build non-zero weekly teaching-hour periods from newest week back until the first zero week.
 */
export function buildWeeklyTeachingHours(
  sessions: TeachingHoursSessionInput[],
  now: Temporal.Instant = Temporal.Now.instant(),
): WeeklyTeachingHoursWeek[] {
  if (sessions.length === 0) {
    return [];
  }

  let latestStart = sessions[0].start;
  for (const session of sessions) {
    if (InstantCompare(session.start).isAfter(latestStart)) {
      latestStart = session.start;
    }
  }

  const weeks: WeeklyTeachingHoursWeek[] = [];
  const latestWeekStart = startOfPacificWeek(latestStart);
  const earliestAllowedWeekStart = startOfPacificWeek(
    latestWeekStart.toZonedDateTimeISO(FrequentTimeZones.UsPacific).subtract({ years: 1 }).toInstant(),
  );
  let cursor = latestWeekStart;

  // Walk backward from the latest session's week until a week with 0 minutes,
  // or until more than 1 year before the latest week.
  while (InstantCompare(cursor).isAfterOrEqual(earliestAllowedWeekStart)) {
    const weekStart = cursor;
    const weekEnd = endOfPacificWeek(weekStart);
    const weekSessions = sessionsInPeriod(sessions, weekStart, weekEnd);
    const minutes = teachingMinutesForSessions(weekSessions, now);

    if (minutes === 0) {
      break;
    }

    weeks.push({ weekStart, weekEnd, minutes, sessions: weekSessions.length });
    cursor = startOfPacificWeek(weekStart.subtract({ hours: 1 }));
  }

  return weeks;
}
